import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CredentialCard = ({ credential, onShare, onView, onRenew }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'text-success bg-success/10 border-success/20';
      case 'expired':
        return 'text-error bg-error/10 border-error/20';
      case 'pending':
        return 'text-warning bg-warning/10 border-warning/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'vaccination':
        return 'Shield';
      case 'insurance':
        return 'Heart';
      case 'training':
        return 'GraduationCap';
      case 'medical':
        return 'Stethoscope';
      case 'license':
        return 'Award';
      default:
        return 'FileText';
    }
  };

  const isExpiringSoon = () => {
    if (!credential?.expirationDate) return false;
    const expirationDate = new Date(credential.expirationDate);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow?.setDate(thirtyDaysFromNow?.getDate() + 30);
    return expirationDate <= thirtyDaysFromNow && credential?.status !== 'expired';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-modal transition-modal">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name={getTypeIcon(credential?.type)} size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground text-lg">{credential?.title}</h3>
            <p className="text-muted-foreground text-sm">{credential?.issuer}</p>
            <div className="flex items-center space-x-2 mt-2">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(credential?.status)}`}>
                <Icon name="CheckCircle" size={12} className="mr-1" />
                {credential?.status?.charAt(0)?.toUpperCase() + credential?.status?.slice(1)}
              </span>
              {credential?.blockchainVerified && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-accent bg-accent/10 border border-accent/20">
                  <Icon name="Link" size={12} className="mr-1" />
                  Blockchain Verified
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-2 hover:bg-muted rounded-lg transition-clinical"
        >
          <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} className="text-muted-foreground" />
        </button>
      </div>
      {/* Expiration Warning */}
      {isExpiringSoon() && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <span className="text-sm font-medium text-warning">Expires Soon</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            This credential expires on {new Date(credential.expirationDate)?.toLocaleDateString()}
          </p>
        </div>
      )}
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-muted-foreground">Issue Date</p>
          <p className="text-sm font-medium text-foreground">
            {new Date(credential.issueDate)?.toLocaleDateString()}
          </p>
        </div>
        {credential?.expirationDate && (
          <div>
            <p className="text-xs text-muted-foreground">Expiration Date</p>
            <p className="text-sm font-medium text-foreground">
              {new Date(credential.expirationDate)?.toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-border pt-4 space-y-4">
          {/* Credential Details */}
          <div>
            <h4 className="font-medium text-foreground mb-2">Credential Details</h4>
            <div className="space-y-2">
              {credential?.details?.map((detail, index) => (
                <div key={index} className="flex justify-between">
                  <span className="text-sm text-muted-foreground">{detail?.label}:</span>
                  <span className="text-sm font-medium text-foreground">{detail?.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Issuer Information */}
          <div>
            <h4 className="font-medium text-foreground mb-2">Issuer Information</h4>
            <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg">
              <Image
                src={credential?.issuerLogo}
                alt={credential?.issuer}
                className="w-10 h-10 rounded-lg object-cover"
              />
              <div>
                <p className="font-medium text-foreground">{credential?.issuer}</p>
                <p className="text-sm text-muted-foreground">{credential?.issuerType}</p>
              </div>
            </div>
          </div>

          {/* Verification Hash */}
          <div>
            <h4 className="font-medium text-foreground mb-2">Verification Hash</h4>
            <div className="bg-muted rounded-lg p-3">
              <p className="text-xs font-mono text-muted-foreground break-all">
                {credential?.verificationHash}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Share"
            iconPosition="left"
            onClick={() => onShare(credential)}
          >
            Share
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="Eye"
            iconPosition="left"
            onClick={() => onView(credential)}
          >
            View
          </Button>
        </div>
        {(credential?.status === 'expired' || isExpiringSoon()) && (
          <Button
            variant="default"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => onRenew(credential)}
          >
            Renew
          </Button>
        )}
      </div>
    </div>
  );
};

export default CredentialCard;